import React, { useState } from "react";
import { InputField } from "../common/input/Input";
import Downshift, { StateChangeOptions } from "downshift";
import { Router } from "../../lib/routes";
import { RouteParams } from "next-routes";
import styled from "styled-components";
import { MagnifingGlass, Cross } from "../common/icons";
import { colors, THEME_SCALE } from "../theme";
import api from "../../lib/clientApi";
import useDidUpdateEffect from "../../lib/useDidUpdateEffect";
import _escapeRegExp from "lodash/escapeRegExp";
import { EmptyResultInfo } from "./EmptyResultsTab";

const MIN_SUGGESTION_LENGTH = 3;
export const SEARCH_INPUT_ID = "search-input";

const SearchIcon = styled(MagnifingGlass)`
  margin-left: calc(4px * 5);
  color: ${colors.white};
  height: 2.5em;
`;

const CleanupIcon = styled(Cross)`
  margin-right: calc(4px * 5);
  height: 2.5em;
  color: ${colors.white};
  flex-shrink: 0;
  cursor: pointer;
`;

const Input = styled(InputField)`
  flex: 1;
  padding: 0;
  &::-webkit-search-cancel-button {
    display: none;
  }
  &::-ms-clear {
    display: none;
  }
  -webkit-appearance: none;
`;
const Container = styled.div`
  width: 100%;
`;
const InputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  white-space: nowrap;
  align-items: center;
  border-bottom: 2px solid ${colors.pigeonGreyLight};
  flex: 1;
  position: relative;
`;
const Suggestions = styled.ul`
  list-style-type: none;
  position: absolute;
  z-index: 1;
  width: 100%;
  top: 2.5em;
  left: 0;
  margin: 0;
  padding: 0;
  background: ${colors.white};
  border-radius: 0 0 4px 4px;
  overflow: hidden;
  box-shadow: 0 60px 80px -16px rgba(0, 69, 106, 0.2);
`;

const Suggestion = styled.li`
  padding: calc(4px * 3) calc((4px * 5) + 26px);
  white-space: normal;
  overflow-wrap: break-word;
  font-size: 1em;
`;

interface Props {
  term: string;
}
export default function SearchInput(props: Props) {
  const [term, setSearchTerm] = useState(props.term);
  const [suggestions, setSuggestions] = useState<
    { value: string; styledValue: string }[]
  >([]);

  useDidUpdateEffect(() => {
    if (props.term === term) return;
    setSearchTerm(props.term);
  }, [props.term]);

  useDidUpdateEffect(() => {
    let isCanceled = false;
    if (!term || suggestions.find(s => s.value === term)) return;
    if (term.length < MIN_SUGGESTION_LENGTH) return setSuggestions([]);

    async function getSuggestions() {
      const results = await api.listSearchSuggestions(term);
      if (isCanceled) return;

      const regex = new RegExp(_escapeRegExp(term), "gi");

      setSuggestions(
        results.slice(0, 8).map(value => ({
          value,
          styledValue: `${value.match(regex)}<b>${value.replace(regex, "")}</b>`
        }))
      );
    }

    getSuggestions();

    return () => {
      isCanceled = true;
    };
  }, [term]);

  const handleStateChange = (changes: StateChangeOptions<string>) => {
    if (changes.highlightedIndex === null) return;
    if (
      changes.type === Downshift.stateChangeTypes.keyDownArrowDown ||
      changes.type === Downshift.stateChangeTypes.keyDownArrowUp
    ) {
      if (suggestions[changes.highlightedIndex])
        setSearchTerm(suggestions[changes.highlightedIndex].value);
    }
  };

  const triggerSearch = (searchTerm: string = term) => {
    const { q, page, ...query } = Router.query;
    if (q === searchTerm) return;

    Router.replaceRoute(Router.route.replace("/", ""), {
      q: searchTerm,
      ...query,
      page: 1
    } as RouteParams);
  };

  const handleCleanup = () => {
    setSearchTerm("");
    setSuggestions([]);
    triggerSearch("");
  };

  return (
    <Container>
      <Downshift
        id="autocomplete-search"
        onChange={suggestion => triggerSearch(suggestion.value)}
        onInputValueChange={term => {
          if (typeof term === "object") return;
          setSearchTerm(term);
        }}
        onStateChange={handleStateChange}
        itemToString={item => item}
        onOuterClick={e => setSearchTerm(e.inputValue)}
        inputValue={term || ""}
        getA11yStatusMessage={({
          isOpen,
          resultCount,
          previousResultCount
        }) => {
          if (!isOpen) {
            return "";
          }

          if (!resultCount) {
            return EmptyResultInfo;
          }

          if (resultCount !== previousResultCount) {
            return `${resultCount} Ergebniss${
              resultCount === 1 ? " ist" : "e sind"
            } verfügbar, bitte benutzen Sie die hoch und runter Pfeil-Tasten zum Navigieren. Drücken Sie die Enter-Taste zum Auswählen.`;
          }

          return "";
        }}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          getRootProps,
          isOpen,
          highlightedIndex,
          closeMenu,
          inputValue
        }) => (
          <InputWrapper {...getRootProps()}>
            <CleanupIcon
              aria-label="Suche löschen"
              onClick={() => {
                closeMenu();
                handleCleanup();
              }}
            />
            <Input
              {...(getInputProps({
                ["aria-label"]: "Suche",
                value: inputValue,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value),
                onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    (e.target as HTMLInputElement).blur();
                    closeMenu();
                    triggerSearch();
                  }
                }
              }) as React.InputHTMLAttributes<HTMLInputElement>)}
              type="text"
              id={SEARCH_INPUT_ID}
            />
            {isOpen &&
            suggestions.length &&
            inputValue.length >= MIN_SUGGESTION_LENGTH ? (
              <Suggestions {...getMenuProps()}>
                {suggestions.map((item, index) => (
                  <Suggestion
                    {...getItemProps({
                      key: item.value,
                      index,
                      item,
                      style: {
                        backgroundColor:
                          highlightedIndex === index
                            ? colors.lightBlue
                            : colors.white
                      }
                    })}
                  >
                    <span
                      dangerouslySetInnerHTML={{ __html: item.styledValue }}
                    />
                  </Suggestion>
                ))}
              </Suggestions>
            ) : null}
            <SearchIcon onClick={() => triggerSearch()} />
          </InputWrapper>
        )}
      </Downshift>
    </Container>
  );
}
